import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Container,
  Fade,
  Chip
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PageTitle from '../components/PageTitle';
import { decryptText, decryptAppId } from '../utils/encryption';
import s3Utils from "../utils/s3Utils";
import ScopeAccreditationCalibration from '../pages/Do/AssessmentReport/Form217/ScopeAccreditationCalibration';

const API_URL = process.env.REACT_APP_API_URL;


const Form73Calibration = () => {
  const location = useLocation();
  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getAssessmentId = () => {
    const queryAssessmentId = getAssessmentIdFromQuery();
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return null;
  };

  const [appId, setAppId] = useState(getAppId());
  const [assessmentId, setAssessmentId] = useState(getAssessmentId());
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [loading, setLoading] = useState(true); // Set to true to load data on mount
  const [reportData, setReportData] = useState(null); // Initialize with null to trigger loading
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Fetch report data from Form217report API
  useEffect(() => {
    if (assessmentId && appId) {
      loadReportData();
    }
  }, [assessmentId, appId]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/nc-main/form73-calibration/${appId}/${assessmentId}`);

      const apiData = response.data;

      if (apiData.isstatus === false) {
        console.warn('API returned error:', apiData.message);
      }

      // Transform API response to match component structure
      const selfiesWithUrls = apiData.selfi ? await Promise.all(
        apiData.selfi.map(async (item) => ({
          LaboratoryName: item.laboratoryName || '',
          Name: item.name || '',
          Capacity: item.capacity || '',
          DateFrom: item.dateFrom || '',
          StartDate: item.startDate || '',
          StartSelfie: item.startSelfie ? await s3Utils.generateSignedUrlForFilename(item.startSelfie) : '',
          StartLat: item.startLatitude || '',
          StartLong: item.startLongitude || '',
          StopDate: item.stopDate || '',
          StopSelfie: item.stopSelfie ? await s3Utils.generateSignedUrlForFilename(item.stopSelfie) : '',
          StopLat: item.stopLatitude || '',
          StopLong: item.stopLongitude || ''
        }))
      ) : [];

      const transformedData = {
        selfies: selfiesWithUrls,

        naf6: {
          lblNAF6LabName: 'Laboratory assessed',
          lblNaf6Lab: apiData.laboratoryName || '',
          lblNaf6QM: apiData.qm || '',
          lblNAF6DateOfVisit: 'Date(s) of Visit',
          lblNaf6Date: apiData.assessmentDate || '',
          lblsubmissionDate: apiData.dateOfSubmission || '',
          lblNAF6TypeOfVisit: 'Type of Visit',
          lblNaf6AssessmenttYPE: apiData.assessmentType || '',
          lblNaf6field: apiData.labType || '',
          lblNaf6Discipline: apiData.disciplines || '',
          lblNaf6Fcility: apiData.facility || '',
          lblNaf6LA: apiData.laName || '',
          rptrNaf6: apiData.naf6?.map(item => ({
            Capacity: item.capacity || '',
            Name: item.name || ''
          })) || [],
          lblNaf6PrevVisitDate: apiData.previousVisitDate || '',
          lblNonConformities: apiData.nonConformities || '',
          lblNaf6AssmtSmry: apiData.assessmentSummary || '',
          lblAssmntTeam: apiData.assessmentTeamComment || '',
          lblNaf6NABL133: apiData.nabL133 || '',
          lblNaf6Nabl142: apiData.nabL142 || '',
          lblNaf6Nabl163: apiData.nabL163 || '',
          lblNaf6Nabl143: apiData.nabL143 || '',
          linbl143: apiData.showNABL143 ? true : false,
          CalibNote: apiData.showCalibrationNote ? true : false,
          lblNonConformty: 'Non-Conformities raised during the assessment',
          lblRecom: '',
          lblNaf6Recommend: apiData.recommendationOfAssessmentTeam || '',
          major: apiData.major || '0',
          minor: apiData.minor || '0',
          majorC4: apiData.majorC4 || '0',
          majorC5: apiData.majorC5 || '0',
          majorC6: apiData.majorC6 || '0',
          majorC7: apiData.majorC7 || '0',
          majorC8: apiData.majorC8 || '0',
          minorC4: apiData.minorC4 || '0',
          minorC5: apiData.minorC5 || '0',
          minorC6: apiData.minorC6 || '0',
          minorC7: apiData.minorC7 || '0',
          minorC8: apiData.minorC8 || '0'
        },

        naf4: apiData.rptr || [],
        naf3a: apiData.naf3a || [],

        naf1a: {
          cabName: apiData.laboratoryName || '',
          openingDate: apiData.openingDate || '',
          openingTime: apiData.openingTime || '',
          closingDate: apiData.closingDate || '',
          closingTime: apiData.closingTime || '',
          attendanceList: apiData.naf1AMeeting?.map(item => ({
            meeting: item.meeting || '',
            labpersonal: item.labPersonal || '',
            designation: item.designation || ''
          })) || []
        },

        form72: apiData.data?.map(item => ({
          AssessorName: item.AssessorName || '',
          LabLocationName: '',
          disciplineName: item.disciplineName || '',
          DisciplineId: item.DisciplineId || '',
          GroupId: '',
          testCode: item.TestCode || '',
          GroupTested: item.GroupTested || '',
          cv: item.CV || '',
          mu: item.MU || '',
          RangeUL: item.RangeUL || '',
          RangeULUnit: item.RangeULUnit || '',
          RangeLL: item.RangeLL || '',
          RangeLLUnit: item.RangeLLUnit || '',
          rangeType: item.RangeType || '',
          testPerformedAt: item.TestPerformedAt || '',
          scopeStandard: item.ScopeStandard || '',
          groupName: item.groupName || '',
          SubGroupId: '',
          subGrpName: '',
          TypeOfTest: item.TypeOfTest || '',
          scopeyear: '',
          unit: `${item.RangeLLUnit || ''}${item.RangeULUnit ? ' - ' + item.RangeULUnit : ''}`,
          measr: item.MU || '',
          status: item.InformationStatus || '',
          EquipmentName: item.EquipmentName || '',
          assessmentTechniqueUsed: item.assessmentTechniqueUsed || item.ScopeStandard || '',
          Remarks: item.Observation || '',
          observation: item.observation || '',
          calibrationTaken: item.calibrationTaken || '',
          conclusion: item.conclusion || '',
          CMC: item.CMC || ''
        })) || [],

        form74: apiData.dec?.map(item => ({
          Name: item.name || '',
          isExEmployee: item.isExEmployee || '0',
          isCabRelation: item.isCabRelation || '0'
        })) || []
      };

      setReportData(transformedData);

    } catch (error) {
      console.error('Error loading report data:', error);
      showSnackbar('Error loading report data: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleGoBack = () => {
    window.history.back();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        gap={2}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="primary">Loading Assessment Report...</Typography>
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Container maxWidth="lg">
        <Box
          p={4}
          mt={4}
          textAlign="center"
          sx={{
            background: 'linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%)',
            borderRadius: 2,
            border: '2px solid #f44336'
          }}
        >
          <AssignmentIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            No Report Data Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Unable to load assessment report. Please try again later.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ p: 2, mt: 10}}>
      <Fade in={true} timeout={800}>
        <Box>
          {/* Scope of Accreditation (Form 72) */}
          {reportData.form72 && reportData.form72.length > 0 && (
            <Fade in={true} timeout={2600}>
              <Box>
                <ScopeAccreditationCalibration data={reportData.form72} labType={reportData?.naf6?.lblNaf6field} />
              </Box>
            </Fade>
          )}

          {/* End of Report */}
          <Fade in={true} timeout={3000}>
            <Paper
              elevation={4}
              sx={{
                mt: 6,
                mb: 4,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                borderRadius: 2,
                border: '2px solid #667eea'
              }}
            >
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 50, color: '#667eea', mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                  End of Report
                </Typography>
              </Box>
            </Paper>
          </Fade>
        </Box>
      </Fade>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Form73Calibration;
